import { useEffect, useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useT } from "@/lib/i18n";
import { useProfile } from "@/lib/api";
import { storyShotsFor } from "@/data/storyMedia";
import { getStoredAdEventType } from "@/lib/quizEventType";
import { ageFromBirthday } from "../age";
import { softGlow } from "../glow";
import type { WelcomeScreenDef, ScreenComponentProps } from "../types";

/**
 * The belonging moment, after the account and the offer. Not a confirmation
 * screen: it names them, hands them a print from a real night, and says out
 * loud that showing up is the part that matters.
 *
 * Its CTA ends the flow — `finish()` saves the answers and routes on to the
 * existing post-signup destination (events / booking / paywall), which are
 * app surfaces of their own rather than onboarding screens.
 */
export default function WelcomeScreen({
  def,
  rt,
}: ScreenComponentProps<WelcomeScreenDef>) {
  const { t } = useT();
  const { data: profileData } = useProfile();
  // The print is of the community they just joined. It used to be a fixed
  // sapphic photo, so a man who had picked men's tables was welcomed with a
  // picture of somebody else's night — on the one screen whose whole job is
  // "you're part of this".
  //
  // Age-matched too, which only this screen can be: the birthday is asked in
  // Act 2, so by the time anyone reaches the welcome we know whether to draw
  // from the 40+ sets. The intro and the reveal both run before that question.
  //
  // Takes the fifth card of the ordered deck rather than the first: the intro
  // conveyor already showed four, and the welcome should not be a repeat of a
  // print they watched go past two minutes ago.
  const age = ageFromBirthday(rt.answers.birthday as string | undefined);
  const print = useMemo(() => {
    const shots = storyShotsFor(rt.audience, getStoredAdEventType(), 5, age, {
      // Same filtered deck the intro draws from, so "the fifth card" is still
      // the fifth card they have not seen.
      distinctTables: true,
    });
    return shots[shots.length - 1];
  }, [rt.audience, age]);
  const firstName = profileData?.data?.user?.firstName as string | undefined;

  /**
   * The one screen in the flow that celebrates rather than asks.
   *
   * The moment is a photograph being handed over, so the motion is that and
   * not a fanfare: the print drops in on a loose spring and settles crooked, a
   * warm bloom flashes behind it as it lands, the image develops from a pale
   * wash into its sepia, and the handwritten caption writes itself on
   * afterwards. Confetti belongs on the booking confirmation, which is a
   * transaction completing; this is someone being welcomed.
   *
   * All of it collapses to a plain fade under `prefers-reduced-motion` — the
   * screen whose whole job is warmth must not be the one that makes somebody
   * feel unwell.
   */
  const reduceMotion = useReducedMotion();

  /**
   * The sequence is driven by a state flip rather than framer's `initial`:
   * every element renders at its "before" values and `play` animates them to
   * their resting ones — a change of target, not an entrance.
   *
   * That began as a workaround (the shell used to wrap screens in
   * `AnimatePresence initial={false}`, whose suppression reached the whole
   * subtree and silenced every entrance on a cold load), and it stays because
   * of the second reason: it waits for the print to decode. The photograph
   * developing is the centrepiece, and on a cold load the image used to arrive
   * after its own animation had finished, so the card popped in empty and the
   * picture blinked in fully developed a moment later. The timeout is the
   * guarantee that a slow or broken image can never hold the celebration
   * hostage.
   */
  const [play, setPlay] = useState(false);
  useEffect(() => {
    let done = false;
    const start = () => {
      if (done) return;
      done = true;
      setPlay(true);
    };
    const img = new Image();
    img.onload = start;
    img.onerror = start;
    img.src = print.img;
    if (img.complete) start();
    const timeout = setTimeout(start, 1200);
    return () => clearTimeout(timeout);
  }, [print.img]);

  // Reduced motion still gets the screen, just all at once.
  const motionOn = play && !reduceMotion;

  return (
    <div className="flex-1 flex flex-col">
      <motion.h2
        animate={play ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
        transition={{ duration: 0.45 }}
        className="font-display font-semibold text-[32px] leading-[1.14] tracking-tight text-neutral-900 dark:text-neutral-50 pt-4"
      >
        {firstName
          ? t(`${def.copyKey}.headline`, { name: firstName })
          : t(`${def.copyKey}.headlineNoName`)}
      </motion.h2>

      <motion.p
        animate={{ opacity: play ? 1 : 0 }}
        transition={{ delay: 0.15, duration: 0.4 }}
        className="text-[15px] leading-relaxed text-neutral-500 dark:text-neutral-400 mt-3"
      >
        {t(`${def.copyKey}.body`)}
      </motion.p>

      <div className="relative self-center mt-6">
        {/* The warmth of the moment, landing with the print. One flash, not a
            loop: something that keeps pulsing behind a photo reads as a
            notification badge. */}
        {/* Drawn rather than blurred, and doubly so here: this one animates its
            opacity and scale, so a filter would have been re-produced on every
            frame of that. See `glow.ts`. */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute -inset-10 rounded-full"
          style={{ background: softGlow("239,98,37", 0.42) }}
          animate={
            motionOn
              ? { opacity: [0, 0.9, 0], scale: [0.5, 1.1, 1.4] }
              : { opacity: 0, scale: 0.5 }
          }
          transition={{ delay: 0.42, duration: 1.4, times: [0, 0.3, 1] }}
        />

        <motion.div
          // A spring loose enough to overshoot: the print arrives, tips past
          // its resting angle and settles. Tweened, it slid into place like a
          // panel; sprung, it reads as something set down on a table.
          animate={
            play
              ? { opacity: 1, scale: 1, rotate: -1.5, y: 0 }
              : reduceMotion
                ? { opacity: 0, rotate: -1.5 }
                : { opacity: 0, scale: 0.82, rotate: -11, y: -26 }
          }
          transition={
            reduceMotion
              ? { duration: 0.3 }
              : {
                  delay: 0.3,
                  type: "spring",
                  stiffness: 170,
                  damping: 13,
                  mass: 0.9,
                }
          }
          className="relative w-[226px] bg-[#F6F2E9] rounded-md p-2.5 pb-8 shadow-[0_26px_60px_rgba(0,0,0,.45)]"
        >
          {/* Developing, the way the print it is pretending to be would: from
              a pale wash into the sepia the rest of the flow's photos wear. */}
          <motion.img
            src={print.img}
            alt=""
            aria-hidden
            className="block w-full h-[214px] object-cover"
            animate={{
              filter:
                play || reduceMotion
                  ? "sepia(.28) saturate(.82) brightness(1) contrast(1)"
                  : "sepia(0) saturate(.25) brightness(1.45) contrast(.8)",
            }}
            transition={{ delay: 0.55, duration: 1.1, ease: "easeOut" }}
          />
          {/* Written on after the photo has developed. `clipPath` rather than
              a fade, so it arrives left to right the way a hand would. */}
          <motion.div
            className="text-center mt-2 text-[#4A4034] font-medium"
            style={{ fontFamily: "Caveat, cursive", fontSize: 19 }}
            animate={{
              clipPath:
                play || reduceMotion ? "inset(0 0% 0 0)" : "inset(0 100% 0 0)",
            }}
            transition={{
              delay: reduceMotion ? 0.2 : 1.15,
              duration: 0.5,
              ease: "easeOut",
            }}
          >
            {t(`${def.copyKey}.caption`)}
          </motion.div>
        </motion.div>
      </div>

      <motion.p
        animate={{ opacity: play ? 1 : 0 }}
        transition={{ delay: reduceMotion ? 0.3 : 1.5, duration: 0.5 }}
        className="text-[15px] leading-relaxed text-neutral-500 dark:text-neutral-400 mt-6"
      >
        {t(`${def.copyKey}.note`)}
      </motion.p>

    </div>
  );
}
